import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Scissors, Package, Trash2, Search, Loader2, Plus, Minus, Users } from 'lucide-react';
import AppButton from '../../components/AppButton/AppButton';
import { HitApi } from '../../Api/ApiHit';
import { searchProduct, searchUser, addBill, updateBill, searchSalonChild } from '../../constant/Constant';
import { getSelectedUnit, getElevateUser } from '../../storage/Storage';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const AddAutoBillServicesNew = forwardRef(({ frequency, currentDate, selectedMembership, appliedCoupon, editMode = null, onServicesUpdate, onPurchaseAmountUpdate }, ref) => {
    const [services, setServices] = useState([]);
    const [products, setProducts] = useState([]);
    const [remarks, setRemarks] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [couponDiscount, setCouponDiscount] = useState(0);
    const [appliedCouponData, setAppliedCouponData] = useState(null);
    const navigate = useNavigate();





    // State for managing frequency from parent
    const [localFrequency, setLocalFrequency] = useState(frequency);

    // Service selection state
    const [showServiceDropdown, setShowServiceDropdown] = useState(false);
    const [availableServices, setAvailableServices] = useState([]);
    const [serviceSearchTerm, setServiceSearchTerm] = useState('');
    const [isLoadingServices, setIsLoadingServices] = useState(false);

    // Product selection state
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);

    // Staff/User state
    const [availableStaff, setAvailableStaff] = useState([]);

    // Collaborator selection state
    const [showCollaboratorDropdown, setShowCollaboratorDropdown] = useState(false);
    const [collaboratorSearchTerm, setCollaboratorSearchTerm] = useState('');
    const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);

    // Collab array to store collaborator user IDs - initialize with logged-in user's ID
    const currentUser = getElevateUser();
    const [collab, setCollab] = useState(() => {
        const userId = currentUser?._id || currentUser?.id;
        return userId ? [userId] : [];
    });

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        updateFrequency: (value) => setLocalFrequency(value)
    }));

    // Ensure logged-in user is always in collab array
    useEffect(() => {
        const userId = currentUser?._id || currentUser?.id;
        if (userId && !collab.includes(userId)) {
            setCollab(prev => [userId, ...prev]);
        }
    }, [currentUser]);

    // Fetch services when dropdown opens
    useEffect(() => {
        if (showServiceDropdown) {
            fetchServices();
        }
    }, [showServiceDropdown, serviceSearchTerm]);

    // Fetch products when dropdown opens
    useEffect(() => {
        if (showProductDropdown) {
            const timer = setTimeout(() => {
                fetchProducts();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [showProductDropdown, productSearchTerm]);

    // Fetch staff on mount
    useEffect(() => {
        fetchStaff();
    }, []);

    // Fetch collaborators when dropdown opens
    useEffect(() => {
        if (showCollaboratorDropdown) {
            const timer = setTimeout(() => {
                fetchCollaborators();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [showCollaboratorDropdown, collaboratorSearchTerm]);

    // Populate data when in edit mode
    useEffect(() => {
        if (editMode) {
            // Populate services from editMode data
            if (editMode.services && Array.isArray(editMode.services)) {
                const mappedServices = editMode.services.map(service => ({
                    _id: service.id,
                    name: service.name,
                    basePrice: service.pricing?.basePrice || 0,
                    price: service.pricing?.finalPrice || service.pricing?.basePrice || 0,
                    quantity: service.quantity || 1,
                    staff: service.staff || null
                }));
                setServices(mappedServices);
            }

            // Populate products from editMode data
            if (editMode.products && Array.isArray(editMode.products)) {
                const mappedProducts = editMode.products.map(product => ({
                    _id: product.id,
                    name: product.name,
                    brand: product.brand || '',
                    basePrice: product.pricing?.basePrice || 0,
                    price: product.pricing?.finalPrice || product.pricing?.basePrice || 0,
                    quantity: product.quantity || 1,
                    staff: product.staff || null
                }));
                setProducts(mappedProducts);
            }

            // Populate collaborators from editMode data
            if (editMode.collab && Array.isArray(editMode.collab)) {
                // Keep existing collaborators from the bill
                setCollab(editMode.collab);
            }

            // Populate remarks if available
            if (editMode.remarks) {
                setRemarks(editMode.remarks);
            }
        }
    }, [editMode]);

    const fetchServices = async () => {
        setIsLoadingServices(true);
        try {
            const json = {
                page: 1,
                limit: 50,
                search: serviceSearchTerm ? {
                    $or: [
                        { name: { $regex: serviceSearchTerm, $options: 'i' } },
                        { category: { $regex: serviceSearchTerm, $options: 'i' } }
                    ],
                    staffIds: [getElevateUser()?._id]
                } : { staffIds: [getElevateUser()?._id] }
            };

            const res = await HitApi(json, searchSalonChild);
            if (res?.statusCode === 200) {
                setAvailableServices(Array.isArray(res?.data?.docs) ? res.data.docs : (Array.isArray(res?.data) ? res.data : []));
            } else {
                setAvailableServices([]);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            toast.error('Failed to load services');
            setAvailableServices([]);
        } finally {
            setIsLoadingServices(false);
        }
    };

    const fetchProducts = async () => {
        setIsLoadingProducts(true);
        try {
            const isBarcode = (searchTerm) => {
                if (!searchTerm || typeof searchTerm !== 'string') return false;
                const cleanTerm = searchTerm.trim().replace(/\s+/g, '');
                return /^\d{6,13}$/.test(cleanTerm) || /^[A-Z0-9]{6,}$/i.test(cleanTerm);
            };

            let searchObj = {};

            if (productSearchTerm && productSearchTerm.trim()) {
                if (isBarcode(productSearchTerm)) {
                    searchObj = { barcode: productSearchTerm.trim() };
                } else {
                    searchObj = {
                        $or: [
                            { productName: { $regex: productSearchTerm, $options: 'i' } },
                            { brand: { $regex: productSearchTerm, $options: 'i' } }
                        ]
                    };
                }
            }

            const json = {
                page: 1,
                limit: 50,
                search: searchObj
            };

            const res = await HitApi(json, searchProduct);
            if (res?.statusCode === 200) {
                setAvailableProducts(Array.isArray(res?.data?.docs) ? res.data.docs : (Array.isArray(res?.data) ? res.data : []));
            } else {
                setAvailableProducts([]);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load products');
            setAvailableProducts([]);
        } finally {
            setIsLoadingProducts(false);
        }
    };

    const fetchStaff = async () => {
        try {
            const json = {
                page: 1,
                limit: 100,
                search: {}
            };
            const res = await HitApi(json, searchUser);
            if (res?.statusCode === 200) {
                setAvailableStaff(Array.isArray(res?.data?.docs) ? res.data.docs : (Array.isArray(res?.data) ? res.data : []));
            } else {
                setAvailableStaff([]);
            }
        } catch (error) {
            console.error('Error fetching staff:', error);
            setAvailableStaff([]);
        }
    };

    const fetchCollaborators = async () => {
        setIsLoadingCollaborators(true);
        try {
            const json = {
                page: 1,
                limit: 50,
                search: collaboratorSearchTerm ? {
                    $or: [
                        { name: { $regex: collaboratorSearchTerm, $options: 'i' } },
                        { email: { $regex: collaboratorSearchTerm, $options: 'i' } },
                        { phone: { $regex: collaboratorSearchTerm, $options: 'i' } }
                    ]
                } : {}
            };
            const res = await HitApi(json, searchUser);
            if (res?.statusCode === 200) {
                setAvailableStaff(Array.isArray(res?.data?.docs) ? res.data.docs : (Array.isArray(res?.data) ? res.data : []));
            } else {
                setAvailableStaff([]);
            }
        } catch (error) {
            console.error('Error fetching collaborators:', error);
            toast.error('Failed to load staff members');
            setAvailableStaff([]);
        } finally {
            setIsLoadingCollaborators(false);
        }
    };

    const handleAddNewService = () => {
        setShowServiceDropdown(true);
        setShowProductDropdown(false);
    };

    const handleAddNewProduct = () => {
        setShowProductDropdown(true);
        setShowServiceDropdown(false);
        setShowCollaboratorDropdown(false);
    };

    const handleAddCollaborator = () => {
        setShowCollaboratorDropdown(true);
        setShowServiceDropdown(false);
        setShowProductDropdown(false);
    };

    const handleSelectService = (service) => {
        const newService = {
            _id: service._id,
            name: service.name,
            parentId: service.parentId,
            duration: service.duration,
            basePrice: service.price || 0,
            price: service.price || 0,
            quantity: 1,
            incentive: service.incentive || 0
        };

        setServices([...services, newService]);
        setShowServiceDropdown(false);
        setServiceSearchTerm('');
        toast.success('Service added');
    };

    const handleSelectProduct = (product) => {
        const newProduct = {
            _id: product._id,
            name: product.productName || product.name,
            brand: product.brand,
            basePrice: product.sellPrice || product.price || 0,
            price: product.sellPrice || product.price || 0,
            mrp: product.mrp || 0,
            costPrice: product.costPrice || 0,
            quantity: 1
        };

        setProducts([...products, newProduct]);
        setShowProductDropdown(false);
        setProductSearchTerm('');
        toast.success('Product added');
    };

    const handleSelectCollaborator = (staff) => {
        // Check if collaborator is already added
        if (collab.includes(staff._id)) {
            toast.error(`${staff.name} is already added as a collaborator`);
            return;
        }

        // Add staff user ID to collab array
        setCollab([...collab, staff._id]);
        setShowCollaboratorDropdown(false);
        setCollaboratorSearchTerm('');
        toast.success(`Collaborator ${staff.name} added`);
    };

    const handleRemoveCollaborator = (userId) => {
        // Prevent removing the logged-in user
        const currentUserId = currentUser?._id || currentUser?.id;
        if (userId === currentUserId) {
            toast.error('You cannot remove yourself from collaborators');
            return;
        }

        const updatedCollab = collab.filter(id => id !== userId);
        setCollab(updatedCollab);
        toast.success('Collaborator removed');
    };

    // Get collaborator details for display
    const getCollaboratorDetails = (userId) => {
        return availableStaff.find(staff => staff._id === userId);
    };

    const handleRemoveService = (index) => {
        const updatedServices = services.filter((_, i) => i !== index);
        setServices(updatedServices);
        toast.success('Service removed');
    };

    const handleRemoveProduct = (index) => {
        const updatedProducts = products.filter((_, i) => i !== index);
        setProducts(updatedProducts);
        toast.success('Product removed');
    };

    const handleServiceQuantityChange = (index, newQuantity) => {
        const quantity = parseInt(newQuantity) || 1;
        if (quantity < 1) return;

        const updatedServices = [...services];
        updatedServices[index] = {
            ...updatedServices[index],
            quantity: quantity
        };
        setServices(updatedServices);
    };

    const handleProductQuantityChange = (index, newQuantity) => {
        const quantity = parseInt(newQuantity) || 1;
        if (quantity < 1) return;

        const updatedProducts = [...products];
        updatedProducts[index] = {
            ...updatedProducts[index],
            quantity: quantity
        };
        setProducts(updatedProducts);
    };


    const handleServicePriceChange = (index, newPrice) => {
        // Allow empty string or any number while typing
        const updatedServices = [...services];
        updatedServices[index] = {
            ...updatedServices[index],
            price: newPrice
        };
        setServices(updatedServices);
    };

    const handleServicePriceBlur = (index) => {
        const service = services[index];
        let price = parseFloat(service.price) || 0;

        // Validate that price is not less than base price
        if (price < service.basePrice) {
            toast.error(`Price cannot be less than base price ₹${service.basePrice}`);
            price = service.basePrice; // Reset to base price
        }

        const updatedServices = [...services];
        updatedServices[index] = {
            ...updatedServices[index],
            price: price
        };
        setServices(updatedServices);
    };

    const handleProductPriceChange = (index, newPrice) => {
        // Allow empty string or any number while typing
        const updatedProducts = [...products];
        updatedProducts[index] = {
            ...updatedProducts[index],
            price: newPrice
        };
        setProducts(updatedProducts);
    };

    const handleProductPriceBlur = (index) => {
        const product = products[index];
        let price = parseFloat(product.price) || 0;

        // Validate that price is not less than base price
        if (price < product.basePrice) {
            toast.error(`Price cannot be less than base price ₹${product.basePrice}`);
            price = product.basePrice; // Reset to base price
        }

        const updatedProducts = [...products];
        updatedProducts[index] = {
            ...updatedProducts[index],
            price: price
        };
        setProducts(updatedProducts);
    };

    const calculateSubtotal = () => {
        const serviceTotal = services.reduce((sum, service) => {
            return sum + (service.price * service.quantity);
        }, 0);
        const productTotal = products.reduce((sum, product) => {
            return sum + (product.price * product.quantity);
        }, 0);
        return serviceTotal + productTotal;
    };

    const calculateGST = (subtotal) => {
        const unit = getSelectedUnit();
        // Only calculate GST if prices are exclusive of taxes
        if (unit?.priceAreInclusiveTaxes === false && unit?.gstPercentage) {
            return (subtotal * unit.gstPercentage) / 100;
        }
        return 0;
    };

    const calculateGrandTotal = () => {
        const subtotal = calculateSubtotal();
        const gst = calculateGST(subtotal);
        return subtotal + gst;
    };

    const handleGenerateAutoBill = async () => {
        // Validation
        if (services.length === 0 && products.length === 0) {
            toast.error('Please add at least one service or product');
            return;
        }


        const unit = getSelectedUnit();
        const user = getElevateUser();

        // Calculate totals (matching QuickSale logic)
        const totalServiceBasePrice = services.reduce((sum, service) => sum + ((service.basePrice || 0) * (service.quantity || 1)), 0);
        const totalProductBasePrice = products.reduce((sum, product) => sum + ((product.basePrice || 0) * (product.quantity || 1)), 0);

        const totalServicePrice = services.reduce((sum, service) => sum + ((service.price || 0) * (service.quantity || 1)), 0);
        const totalProductPrice = products.reduce((sum, product) => sum + ((product.price || 0) * (product.quantity || 1)), 0);

        const totalServiceDiscount = totalServiceBasePrice - totalServicePrice;
        const totalProductDiscount = totalProductBasePrice - totalProductPrice;
        const totalDiscount = totalServiceDiscount + totalProductDiscount;

        const subtotal = totalServicePrice + totalProductPrice;
        const subtotalAfterCoupon = subtotal - couponDiscount;

        // Calculate GST if prices are exclusive of taxes
        let gstAmount = 0;
        let totalWithGst = subtotalAfterCoupon;

        if (unit?.priceAreInclusiveTaxes === false && unit?.gstPercentage) {
            gstAmount = (subtotalAfterCoupon * unit.gstPercentage) / 100;
            totalWithGst = subtotalAfterCoupon + gstAmount;
        }

        // Round-off logic to make bill multiple of 10 (same as QuickSale)
        const roundedTotal = Math.round(totalWithGst / 10) * 10;
        const roundOffAmount = roundedTotal - totalWithGst;
        const grandTotal = roundedTotal;

        // Generate minimal billing data (only required fields)
        const billingData = {
            // Basic billing info
            billDate: new Date().toLocaleDateString('en-IN'),
            billTime: new Date().toLocaleTimeString('en-IN'),
            timestamp: new Date().toISOString(),

            // Auto Billing Specific Fields
            autoBillingStatus: true,
            frequency: localFrequency,
            billingStatus: false, // false = pending
            status: 'pending', // pending status
            billType: 'AUTO_SALE',

            // Services with basic structure
            services: services.map(service => ({
                id: service._id,
                name: service.name,
                quantity: service.quantity,
                pricing: {
                    basePrice: service.basePrice,
                    finalPrice: service.price,
                    totalPrice: service.price * service.quantity
                },
                // Use service's staff ID if exists (for collaborators), otherwise use logged-in user ID
                staff: service.staff || user?._id || user?.id || null
            })),

            // Products with basic structure
            products: products.map(product => ({
                id: product._id,
                name: product.name,
                brand: product.brand,
                quantity: product.quantity,
                pricing: {
                    basePrice: product.basePrice,
                    finalPrice: product.price,
                    totalPrice: product.price * product.quantity
                },
                // Use product's staff ID if exists, otherwise use logged-in user ID
                staff: product.staff || user?._id || user?.id || null
            })),

            // Payment - ALL ZERO for auto billing (pending payment)
            payment: {
                activePaymentMethods: [],
                totalPaid: 0,
                paymentStatus: 'Pending'
            },

            // Calculations - only totals
            calculations: {
                totals: {
                    finalAmount: grandTotal
                }
            },

            // Required backend fields
            createdBy: user?._id || user?.id || '000000000000000000000000',
            unitId: unit?._id || unit,

            // Collaborators array - stores user IDs
            collab: collab
        };

        // Add remarks if provided
        if (remarks.trim()) {
            billingData.remarks = remarks.trim();
        }

        console.log('=== AUTO BILLING PAYLOAD (Matching QuickSale) ===');
        console.log(JSON.stringify(billingData, null, 2));
        console.log('=================================================');

        try {
            setIsGenerating(true);

            // Check if we're in edit mode
            const isEditMode = editMode && editMode._id;

            if (isEditMode) {
                toast.loading('Updating bill...', { id: 'auto-bill-generation' });

                // Add bill ID for update
                const updateData = {
                    ...billingData,
                    _id: editMode._id
                };

                console.log("updateData", updateData);


                const response = await HitApi(updateData, updateBill);

                if (response?.statusCode === 200 || response?.statusCode === 201) {
                    toast.success('Bill updated successfully!', {
                        id: 'auto-bill-generation',
                        duration: 4000
                    });

                    console.log('=== BILL UPDATED ===');
                    console.log('Response:', response);
                    console.log('====================');

                    // Redirect to bill list or previous page
                    setTimeout(() => {
                        window.history.back();
                    }, 1000);

                } else {
                    toast.error(response?.message || 'Failed to update bill', {
                        id: 'auto-bill-generation',
                        duration: 4000
                    });

                    console.error('=== BILL UPDATE FAILED ===');
                    console.error('Response:', response);
                    console.error('==========================');
                }
            } else {
                toast.loading('Creating Staff Billing...', { id: 'auto-bill-generation' });

                const response = await HitApi(billingData, addBill);

                if (response?.statusCode === 200 || response?.statusCode === 201) {
                    toast.success('Staff Billing created successfully!', {
                        id: 'auto-bill-generation',
                        duration: 4000
                    });

                    navigate('/staffBilling');
                    setServices([]);
                    setProducts([]);
                    setRemarks('');

                } else {
                    toast.error(response?.message || 'Failed to create Staff Billing', {
                        id: 'auto-bill-generation',
                        duration: 4000
                    });

                    console.error('=== AUTO BILLING FAILED ===');
                    console.error('Response:', response);
                    console.error('============================');
                }
            }

        } catch (error) {
            console.error('=== AUTO BILLING ERROR ===');
            console.error('Error:', error);
            console.error('===========================');

            toast.error('Network error. Please try again.', {
                id: 'auto-bill-generation',
                duration: 4000
            });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="space-y-4 ">
            {/* Action Buttons - Mobile Responsive */}
            <div className="grid grid-cols-3 gap-1.5 md:gap-4">
                {/* Add Service Button */}
                <button
                    onClick={handleAddNewService}
                    className="md:hidden flex items-center justify-center gap-1 px-2 py-2.5 text-xs font-semibold text-white bg-[#1e3a8a] rounded-lg shadow-sm active:scale-[0.98] transition-all"
                >
                    Service
                </button>
                <div className="hidden md:block">
                    <AppButton
                        buttontext="Add Service"
                        onClick={handleAddNewService}
                        icon={Scissors}
                        iconPosition="left"
                        className="w-full py-4 text-base shadow-sm active:scale-[0.98] transition-all justify-center"
                    />
                </div>

                {/* Add Product Button */}
                <button
                    onClick={handleAddNewProduct}
                    className="md:hidden flex items-center justify-center gap-1 px-2 py-2.5 text-xs font-semibold text-white bg-[#10b981] rounded-lg shadow-sm active:scale-[0.98] transition-all"
                >
                    Product
                </button>
                <div className="hidden md:block">
                    <AppButton
                        buttontext="Add Product"
                        onClick={handleAddNewProduct}
                        icon={Package}
                        iconPosition="left"
                        className="w-full py-4 text-base shadow-sm active:scale-[0.98] transition-all justify-center"
                        backgroundColor="#10b981"
                        useCustomColors={true}
                    />
                </div>

                {/* Add Collaborator Button */}
                <button
                    onClick={handleAddCollaborator}
                    className="md:hidden flex items-center justify-center gap-1 px-2 py-2.5 text-xs font-semibold text-white bg-[#f59e0b] rounded-lg shadow-sm active:scale-[0.98] transition-all"
                >
                    Collaborator
                </button>
                <div className="hidden md:block">
                    <AppButton
                        buttontext="Add Collaborator"
                        onClick={handleAddCollaborator}
                        icon={Users}
                        iconPosition="left"
                        className="w-full py-4 text-base shadow-sm active:scale-[0.98] transition-all justify-center"
                        backgroundColor="#f59e0b"
                        useCustomColors={true}
                    />
                </div>
            </div>

            {/* Service Selection Dropdown */}
            {showServiceDropdown && (
                <div className="bg-white border border-gray-200 rounded-lg p-1.5 md:p-4">
                    <div className="flex items-center justify-between mb-1 md:mb-3">
                        <h3 className="text-[10px] md:text-sm font-semibold text-gray-900">Select Service</h3>
                        <button
                            onClick={() => setShowServiceDropdown(false)}
                            className="text-gray-400 hover:text-gray-600 p-0.5"
                        >
                            <Plus className="h-3.5 w-3.5 md:h-5 md:w-5 rotate-45" />
                        </button>
                    </div>

                    <div className="relative mb-1.5 md:mb-4">
                        <Search className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 h-3 w-3 md:h-5 md:w-5 text-gray-400" />
                        <input
                            type="text"
                            value={serviceSearchTerm}
                            onChange={(e) => setServiceSearchTerm(e.target.value)}
                            placeholder="Search..."
                            className="w-full pl-6 md:pl-12 pr-2 md:pr-4 py-1.5 md:py-3.5 border border-gray-200 rounded-md md:rounded-xl focus:ring-1 md:focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-base transition-all"
                            autoFocus
                        />
                    </div>

                    <div className="max-h-96 md:max-h-[600px] overflow-y-auto">
                        {isLoadingServices ? (
                            <div className="flex items-center justify-center py-3 md:py-8">
                                <Loader2 className="h-4 w-4 md:h-6 md:w-6 text-blue-500 animate-spin" />
                            </div>
                        ) : availableServices.length > 0 ? (
                            <div className="space-y-0.5 md:space-y-2">
                                {availableServices.map((service) => (
                                    <button
                                        key={service._id}
                                        onClick={() => handleSelectService(service)}
                                        className="w-full text-left p-1.5 md:p-4 hover:bg-blue-50/50 rounded-md md:rounded-xl transition-all border border-gray-100 hover:border-blue-200 active:scale-[0.99]"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs md:text-base font-semibold text-gray-900 leading-tight">{service.name}</p>
                                                {service.parentId?.name && (
                                                    <span className="text-[8px] md:text-xs font-medium text-blue-600 bg-blue-50 px-1 md:px-2.5 py-0.5 md:py-1 rounded mt-0.5 inline-block">
                                                        {service.parentId.name}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs md:text-base font-bold text-blue-600 bg-blue-50 px-1.5 md:px-3 py-0.5 md:py-1 rounded-md md:rounded-lg">
                                                ₹{service.price || 0}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-gray-500 py-3 md:py-8 text-[10px] md:text-sm">No services found</p>
                        )}
                    </div>
                </div>
            )}

            {/* Product Selection Dropdown */}
            {showProductDropdown && (
                <div className="bg-white border border-gray-200 rounded-lg p-1.5 md:p-4">
                    <div className="flex items-center justify-between mb-1 md:mb-3">
                        <h3 className="text-[10px] md:text-sm font-semibold text-gray-900">Select Product</h3>
                        <button
                            onClick={() => setShowProductDropdown(false)}
                            className="text-gray-400 hover:text-gray-600 p-0.5"
                        >
                            <Plus className="h-3.5 w-3.5 md:h-5 md:w-5 rotate-45" />
                        </button>
                    </div>

                    <div className="relative mb-1.5 md:mb-4">
                        <Search className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 h-3 w-3 md:h-5 md:w-5 text-gray-400" />
                        <input
                            type="text"
                            value={productSearchTerm}
                            onChange={(e) => setProductSearchTerm(e.target.value)}
                            placeholder="Search..."
                            className="w-full pl-6 md:pl-12 pr-2 md:pr-4 py-1.5 md:py-3.5 border border-gray-200 rounded-md md:rounded-xl focus:ring-1 md:focus:ring-4 focus:ring-green-100 focus:border-green-500 text-base transition-all"
                            autoFocus
                        />
                    </div>

                    <div className="max-h-96 md:max-h-[600px] overflow-y-auto">
                        {isLoadingProducts ? (
                            <div className="flex items-center justify-center py-3 md:py-8">
                                <Loader2 className="h-4 w-4 md:h-6 md:w-6 text-green-500 animate-spin" />
                            </div>
                        ) : availableProducts.length > 0 ? (
                            <div className="space-y-0.5 md:space-y-2">
                                {availableProducts.map((product) => (
                                    <button
                                        key={product._id}
                                        onClick={() => handleSelectProduct(product)}
                                        className="w-full text-left p-1.5 md:p-4 hover:bg-green-50/50 rounded-md md:rounded-xl transition-all border border-gray-100 hover:border-green-200 active:scale-[0.99]"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs md:text-base font-semibold text-gray-900 leading-tight">{product.productName}</p>
                                                {product.brand && (
                                                    <span className="text-[8px] md:text-xs font-medium text-green-600 bg-green-50 px-1 md:px-2.5 py-0.5 md:py-1 rounded mt-0.5 inline-block">
                                                        {product.brand}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs md:text-base font-bold text-green-600 bg-green-50 px-1.5 md:px-3 py-0.5 md:py-1 rounded-md md:rounded-lg">
                                                ₹{product.sellPrice || product.price || 0}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-gray-500 py-3 md:py-8 text-[10px] md:text-sm">No products found</p>
                        )}
                    </div>
                </div>
            )}

            {/* Collaborator Selection Dropdown */}
            {showCollaboratorDropdown && (
                <div className="bg-white border border-gray-200 rounded-lg p-1.5 md:p-4">
                    <div className="flex items-center justify-between mb-1 md:mb-3">
                        <h3 className="text-[10px] md:text-sm font-semibold text-gray-900">Select Collaborator</h3>
                        <button
                            onClick={() => setShowCollaboratorDropdown(false)}
                            className="text-gray-400 hover:text-gray-600 p-0.5"
                        >
                            <Plus className="h-3.5 w-3.5 md:h-5 md:w-5 rotate-45" />
                        </button>
                    </div>

                    <div className="relative mb-1.5 md:mb-4">
                        <Search className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 h-3 w-3 md:h-5 md:w-5 text-gray-400" />
                        <input
                            type="text"
                            value={collaboratorSearchTerm}
                            onChange={(e) => setCollaboratorSearchTerm(e.target.value)}
                            placeholder="Search..."
                            className="w-full pl-6 md:pl-12 pr-2 md:pr-4 py-1.5 md:py-3.5 border border-gray-200 rounded-md md:rounded-xl focus:ring-1 md:focus:ring-4 focus:ring-orange-100 focus:border-orange-500 text-base transition-all"
                            autoFocus
                        />
                    </div>

                    <div className="max-h-96 md:max-h-[600px] overflow-y-auto">
                        {isLoadingCollaborators ? (
                            <div className="flex items-center justify-center py-3 md:py-8">
                                <Loader2 className="h-4 w-4 md:h-6 md:w-6 text-orange-500 animate-spin" />
                            </div>
                        ) : availableStaff.length > 0 ? (
                            <div className="space-y-0.5 md:space-y-2">
                                {availableStaff
                                    .filter(staff => {
                                        const alreadyAdded = collab.includes(staff._id);
                                        const currentUserId = getElevateUser()?._id;
                                        const isCurrentUser = staff._id === currentUserId;
                                        return !alreadyAdded && !isCurrentUser;
                                    })
                                    .map((staff) => (
                                        <button
                                            key={staff._id}
                                            onClick={() => handleSelectCollaborator(staff)}
                                            className="w-full text-left p-1.5 md:p-4 hover:bg-orange-50/50 rounded-md md:rounded-xl transition-all border border-gray-100 hover:border-orange-200 active:scale-[0.99]"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-xs md:text-base font-semibold text-gray-900 leading-tight">{staff.name}</p>
                                                    {staff.email && (
                                                        <span className="text-[8px] md:text-xs font-medium text-orange-600 bg-orange-50 px-1 md:px-2.5 py-0.5 md:py-1 rounded mt-0.5 inline-block">
                                                            {staff.email}
                                                        </span>
                                                    )}
                                                </div>
                                                <Users className="h-3 w-3 md:h-5 md:w-5 text-orange-500" />
                                            </div>
                                        </button>
                                    ))}
                                {availableStaff.filter(staff => {
                                    const alreadyAdded = collab.includes(staff._id);
                                    const currentUserId = getElevateUser()?._id;
                                    const isCurrentUser = staff._id === currentUserId;
                                    return !alreadyAdded && !isCurrentUser;
                                }).length === 0 && (
                                        <p className="text-center text-gray-500 py-3 md:py-8 text-[10px] md:text-sm">All staff added</p>
                                    )}
                            </div>
                        ) : (
                            <p className="text-center text-gray-500 py-3 md:py-8 text-[10px] md:text-sm">No staff found</p>
                        )}
                    </div>
                </div>
            )}

            {/* Added Collaborators List - excluding logged-in user from display */}
            {collab.filter(userId => {
                const currentUserId = currentUser?._id || currentUser?.id;
                return userId !== currentUserId;
            }).length > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 px-1">
                            <Users className="h-4 w-4 text-orange-500" />
                            Added Collaborators ({collab.filter(userId => {
                                const currentUserId = currentUser?._id || currentUser?.id;
                                return userId !== currentUserId;
                            }).length})
                        </h3>
                        <div className="space-y-2">
                            {collab
                                .filter(userId => {
                                    const currentUserId = currentUser?._id || currentUser?.id;
                                    return userId !== currentUserId;
                                })
                                .map((userId, index) => {
                                    const staffDetails = getCollaboratorDetails(userId);
                                    return (
                                        <div key={userId} className="bg-white rounded-xl p-3 border border-orange-100 shadow-sm flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {staffDetails?.name || `Collaborator ${index + 1}`}
                                                    </p>
                                                    {staffDetails?.email && (
                                                        <p className="text-xs text-gray-500">{staffDetails.email}</p>
                                                    )}
                                                    {staffDetails?.phone && (
                                                        <p className="text-xs text-gray-500">{staffDetails.phone}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveCollaborator(userId)}
                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                            >
                                                <Trash2 className="h-5 w-5" />
                                            </button>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}

            {/* Services List */}
            {services.length > 0 && (
                <div className="space-y-1.5 md:space-y-3">
                    <h3 className="text-[10px] md:text-sm font-bold text-gray-900 flex items-center gap-1 md:gap-2 px-0.5 md:px-1">
                        <Scissors className="h-3 w-3 md:h-4 md:w-4 text-blue-500" />
                        Services ({services.length})
                    </h3>
                    <div className="space-y-1.5 md:space-y-3">
                        {services.map((service, index) => (
                            <div key={index} className="bg-white rounded-lg md:rounded-2xl p-2 md:p-4 border border-gray-100 shadow-sm relative overflow-hidden">
                                {/* Mobile Layout - Compact Single Row */}
                                <div className="md:hidden">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-gray-900 truncate">{service.name}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] text-gray-400 line-through">₹{service.basePrice}</span>
                                                <div className="flex items-center">
                                                    <span className="text-[10px] text-gray-500">₹</span>
                                                    <input
                                                        type="number"
                                                        value={service.price}
                                                        onChange={(e) => handleServicePriceChange(index, e.target.value)}
                                                        onBlur={() => handleServicePriceBlur(index)}
                                                        className="w-14 px-1 py-0.5 bg-gray-50 border border-gray-200 rounded text-xs font-medium text-gray-900 focus:ring-1 focus:ring-blue-500"
                                                        min={service.basePrice}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 bg-gray-50 rounded-md border border-gray-200 p-0.5">
                                            <button
                                                onClick={() => handleServiceQuantityChange(index, service.quantity - 1)}
                                                className="p-1 hover:bg-white rounded text-gray-600"
                                            >
                                                <Minus className="h-3 w-3" />
                                            </button>
                                            <span className="w-5 text-center text-xs font-bold">{service.quantity}</span>
                                            <button
                                                onClick={() => handleServiceQuantityChange(index, service.quantity + 1)}
                                                className="p-1 hover:bg-white rounded text-gray-600"
                                            >
                                                <Plus className="h-3 w-3" />
                                            </button>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs font-bold text-blue-600">₹{(service.price * service.quantity).toFixed(0)}</p>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveService(index)}
                                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Desktop Layout - Original */}
                                <div className="hidden md:block">
                                    <div className="absolute top-0 right-0 p-2">
                                        <button
                                            onClick={() => handleRemoveService(index)}
                                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <div className="pr-10 mb-4">
                                        <p className="text-base font-bold text-gray-900 leading-tight">{service.name}</p>
                                        <p className="text-sm text-blue-600 font-medium mt-1">₹{service.price}</p>
                                    </div>

                                    <div className="flex items-end justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex flex-col items-start mb-1">
                                                <span className="text-xs text-gray-500 line-through">Base: ₹{service.basePrice}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-500 font-medium">₹</span>
                                                <input
                                                    type="number"
                                                    value={service.price}
                                                    onChange={(e) => handleServicePriceChange(index, e.target.value)}
                                                    onBlur={() => handleServicePriceBlur(index)}
                                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                    min={service.basePrice}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block text-center">Qty</label>
                                            <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 p-1">
                                                <button
                                                    onClick={() => handleServiceQuantityChange(index, service.quantity - 1)}
                                                    className="p-2 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600"
                                                >
                                                    <Minus className="h-4 w-4" />
                                                </button>
                                                <input
                                                    type="number"
                                                    value={service.quantity}
                                                    onChange={(e) => handleServiceQuantityChange(index, e.target.value)}
                                                    className="w-12 text-center bg-transparent border-none p-0 text-sm font-bold focus:ring-0"
                                                    min="1"
                                                />
                                                <button
                                                    onClick={() => handleServiceQuantityChange(index, service.quantity + 1)}
                                                    className="p-2 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-t border-gray-50 flex justify-between items-center">
                                        <span className="text-xs font-medium text-gray-500">Total Amount</span>
                                        <span className="text-base font-bold text-gray-900">₹{(service.price * service.quantity).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Products List */}
            {products.length > 0 && (
                <div className="space-y-1.5 md:space-y-3">
                    <h3 className="text-[10px] md:text-sm font-bold text-gray-900 flex items-center gap-1 md:gap-2 px-0.5 md:px-1">
                        <Package className="h-3 w-3 md:h-4 md:w-4 text-green-500" />
                        Products ({products.length})
                    </h3>
                    <div className="space-y-1.5 md:space-y-3">
                        {products.map((product, index) => (
                            <div key={index} className="bg-white rounded-lg md:rounded-2xl p-2 md:p-4 border border-gray-100 shadow-sm relative overflow-hidden">
                                {/* Mobile Layout - Compact Single Row */}
                                <div className="md:hidden">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-gray-900 truncate">{product.name}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] text-gray-400 line-through">₹{product.basePrice}</span>
                                                <div className="flex items-center">
                                                    <span className="text-[10px] text-gray-500">₹</span>
                                                    <input
                                                        type="number"
                                                        value={product.price}
                                                        onChange={(e) => handleProductPriceChange(index, e.target.value)}
                                                        onBlur={() => handleProductPriceBlur(index)}
                                                        className="w-14 px-1 py-0.5 bg-gray-50 border border-gray-200 rounded text-xs font-medium text-gray-900 focus:ring-1 focus:ring-green-500"
                                                        min={product.basePrice}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 bg-gray-50 rounded-md border border-gray-200 p-0.5">
                                            <button
                                                onClick={() => handleProductQuantityChange(index, product.quantity - 1)}
                                                className="p-1 hover:bg-white rounded text-gray-600"
                                            >
                                                <Minus className="h-3 w-3" />
                                            </button>
                                            <span className="w-5 text-center text-xs font-bold">{product.quantity}</span>
                                            <button
                                                onClick={() => handleProductQuantityChange(index, product.quantity + 1)}
                                                className="p-1 hover:bg-white rounded text-gray-600"
                                            >
                                                <Plus className="h-3 w-3" />
                                            </button>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs font-bold text-green-600">₹{(product.price * product.quantity).toFixed(0)}</p>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveProduct(index)}
                                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Desktop Layout - Original */}
                                <div className="hidden md:block">
                                    <div className="absolute top-0 right-0 p-2">
                                        <button
                                            onClick={() => handleRemoveProduct(index)}
                                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <div className="pr-10 mb-4">
                                        <p className="text-base font-bold text-gray-900 leading-tight">{product.name}</p>
                                        {product.brand && (
                                            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded mt-1 inline-block">
                                                {product.brand}
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-end justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex flex-col items-start mb-1">
                                                <span className="text-xs text-gray-500 line-through">Base: ₹{product.basePrice}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-500 font-medium">₹</span>
                                                <input
                                                    type="number"
                                                    value={product.price}
                                                    onChange={(e) => handleProductPriceChange(index, e.target.value)}
                                                    onBlur={() => handleProductPriceBlur(index)}
                                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                                    min={product.basePrice}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block text-center">Qty</label>
                                            <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 p-1">
                                                <button
                                                    onClick={() => handleProductQuantityChange(index, product.quantity - 1)}
                                                    className="p-2 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600"
                                                >
                                                    <Minus className="h-4 w-4" />
                                                </button>
                                                <input
                                                    type="number"
                                                    value={product.quantity}
                                                    onChange={(e) => handleProductQuantityChange(index, e.target.value)}
                                                    className="w-12 text-center bg-transparent border-none p-0 text-sm font-bold focus:ring-0"
                                                    min="1"
                                                />
                                                <button
                                                    onClick={() => handleProductQuantityChange(index, product.quantity + 1)}
                                                    className="p-2 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-t border-gray-50 flex justify-between items-center">
                                        <span className="text-xs font-medium text-gray-500">Total Amount</span>
                                        <span className="text-base font-bold text-gray-900">₹{(product.price * product.quantity).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Bill Summary */}
            {(services.length > 0 || products.length > 0) && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 md:p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] lg:static lg:shadow-none lg:border-t lg:pt-4 lg:p-0 z-50">
                    <div className="max-w-7xl mx-auto lg:max-w-none">
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-4 hidden lg:block">
                            <h3 className="text-sm font-bold text-gray-900 mb-3">Bill Summary</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Subtotal:</span>
                                    <span className="font-medium">₹{calculateSubtotal().toFixed(2)}</span>
                                </div>
                                {getSelectedUnit()?.priceAreInclusiveTaxes === false && getSelectedUnit()?.gstPercentage > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">GST ({getSelectedUnit()?.gstPercentage}%):</span>
                                        <span className="font-medium">₹{calculateGST(calculateSubtotal()).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2 mt-2">
                                    <span>Grand Total:</span>
                                    <span className="text-purple-600">₹{calculateGrandTotal().toFixed(2)}</span>
                                </div>
                                {getSelectedUnit()?.priceAreInclusiveTaxes !== false && (
                                    <p className="text-xs text-gray-500 text-center">Including of all taxes</p>
                                )}
                            </div>
                        </div>

                        {/* Mobile Summary (Compact) */}
                        <div className="flex items-center gap-2 mb-1.5 lg:hidden">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="text-[10px] text-gray-500 font-medium">Total:</p>
                                    <p className="text-sm font-bold text-gray-900">₹{calculateGrandTotal().toFixed(2)}</p>
                                    {getSelectedUnit()?.priceAreInclusiveTaxes === false && getSelectedUnit()?.gstPercentage > 0 && (
                                        <span className="text-[9px] text-gray-400">(+{getSelectedUnit()?.gstPercentage}% GST)</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    placeholder="Remarks..."
                                    className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-purple-500 focus:border-transparent text-xs bg-gray-50"
                                />
                            </div>
                        </div>

                        {/* Desktop Remarks */}
                        <div className="mb-4 lg:mb-0 hidden lg:block">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Remarks (Optional)
                            </label>
                            <textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Add any additional notes..."
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-gray-50"
                                rows="2"
                            />
                        </div>

                        <div className="mt-1.5 lg:mt-4">
                            <button
                                onClick={handleGenerateAutoBill}
                                disabled={isGenerating}
                                className="w-full px-4 md:px-6 py-2.5 md:py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg md:rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 text-xs md:text-base font-bold shadow-lg shadow-purple-200 active:scale-[0.98]"
                            >
                                {isGenerating ? (
                                    <span className="flex items-center justify-center gap-1.5 md:gap-2">
                                        <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                                        {editMode ? 'Updating...' : 'Creating...'}
                                    </span>
                                ) : (editMode ? 'Update Bill' : 'Create Bill')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* 
            {(services.length > 0 || products.length > 0) && (
                <div className="h-48 lg:hidden" />
            )} */}
        </div>
    );
});

export default AddAutoBillServicesNew;
